import { useCallback, useEffect, useState, type MouseEvent } from 'react';

import { api } from '../lib/api.js';
import { rawUrl } from '../lib/thumbnails.js';
import { Icon } from './ui/icons.js';

/**
 * Pictures and documents in the chat. Both directions they are project files
 * under `.prism/chat/`, named in the message text by tags:
 *
 *   [image: .prism/chat/…png]   shown as the picture
 *   [file: .prism/chat/…txt]    shown as a file chip
 *
 * The user's pasted / dropped files are saved there by the composer; the
 * worker saves tool screenshots there and the assistant writes the same tags
 * to show a picture (apps/worker/src/chat-media.ts).
 */

const TAG = /\[(image|file):\s*([^\]\n]+?)\s*\]/g;
const MEDIA_DIR = '.prism/chat';
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const TEXT_EXT = /\.(txt|md|markdown|json|csv|tsv|log|rhai|xml|ya?ml|toml|ini|cfg|conf|js|mjs|ts|tsx|jsx|py|rs|lua|glsl|wgsl|hlsl|html?|css|sh|bat|ps1|sql)$/i;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 1024 * 1024;

export interface ChatMedia {
  readonly body: string;
  readonly images: readonly string[];
  readonly files: readonly string[];
}

/** Splits a chat line into its text and its picture / document tags. */
export function splitMedia(text: string): ChatMedia {
  const images: string[] = [];
  const files: string[] = [];
  const body = text
    .replace(TAG, (_tag, kind: string, path: string) => {
      (kind === 'image' ? images : files).push(path);
      return '';
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { body, images, files };
}

/** A file's name without the `.prism/chat/<stamp>-<id>-` prefix the composer adds. */
function displayName(path: string): string {
  const base = path.split('/').pop() ?? path;
  return base.replace(/^\d{4}-\d{2}-\d{2}-[a-z0-9]{6}-/, '');
}

/** Pictures and document chips of one chat line; a picture opens large on click. */
export function MediaStrip({ projectId, images, files }: { projectId: number; images: readonly string[]; files: readonly string[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!images.length && !files.length) return null;
  // Inside a <summary>: a click on a picture must not fold the details open.
  const stop = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <>
      <div className="chat-media">
        {images.map((path) => (
          <button
            key={path}
            type="button"
            className="chat-media-image"
            title={`${displayName(path)} (click to enlarge)`}
            onClick={(event) => {
              stop(event);
              setOpen(path);
            }}
          >
            <img src={rawUrl(projectId, path)} alt={displayName(path)} loading="lazy" />
          </button>
        ))}
        {files.map((path) => (
          <a
            key={path}
            className="chat-media-file"
            href={rawUrl(projectId, path)}
            target="_blank"
            rel="noreferrer"
            title={path}
            onClick={(event) => event.stopPropagation()}
          >
            <Icon name="file" size={12} />
            {displayName(path)}
          </a>
        ))}
      </div>
      {open && <ImageViewer src={rawUrl(projectId, open)} name={displayName(open)} onClose={() => setOpen(null)} />}
    </>
  );
}

function ImageViewer({ src, name, onClose }: { src: string; name: string; onClose: () => void }) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);
  return (
    <div className="chat-viewer" role="dialog" aria-label={name} onClick={onClose}>
      <img src={src} alt={name} onClick={(event) => event.stopPropagation()} />
      <button type="button" className="chat-viewer-close" aria-label="Close" onClick={onClose}>
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

export interface Attachment {
  readonly id: string;
  readonly kind: 'image' | 'file';
  readonly name: string;
  /** Project path once uploaded. */
  readonly path: string | null;
  /** Local preview while it uploads. */
  readonly preview: string | null;
}

/** Whether the composer takes this file, and as what. */
function attachmentKind(file: File): 'image' | 'file' | null {
  if (IMAGE_TYPES.has(file.type)) return 'image';
  if (file.type.startsWith('text/') || TEXT_EXT.test(file.name)) return 'file';
  return null;
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function mediaPath(name: string, kind: 'image' | 'file', type: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const id = Math.random().toString(36).slice(2, 8).padEnd(6, '0');
  let safe = name.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').slice(-60) || (kind === 'image' ? 'image' : 'document');
  // A pasted screenshot has no name: give it one with the right extension.
  if (kind === 'image' && !/\.(png|jpe?g|webp|gif)$/i.test(safe)) safe = `${safe}.${type.split('/')[1] === 'jpeg' ? 'jpg' : (type.split('/')[1] ?? 'png')}`;
  if (kind === 'file' && !/\.[a-z0-9]+$/i.test(safe)) safe = `${safe}.txt`;
  return `${MEDIA_DIR}/${stamp}-${id}-${safe}`;
}

/** Pictures and documents waiting in the composer: pasted or dropped, uploaded right away. */
export function useAttachments(projectId: number | null) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const add = useCallback(
    (files: readonly File[]) => {
      if (!projectId) return;
      setError(null);
      for (const file of files) {
        const kind = attachmentKind(file);
        if (!kind) {
          setError(`${file.name || 'That file'} is not an image or a text document.`);
          continue;
        }
        if (file.size > (kind === 'image' ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES)) {
          setError(`${file.name || 'That file'} is too large (${kind === 'image' ? '8 MB' : '1 MB'} at most).`);
          continue;
        }
        const id = Math.random().toString(36).slice(2);
        const preview = kind === 'image' ? URL.createObjectURL(file) : null;
        setItems((list) => [...list, { id, kind, name: file.name || (kind === 'image' ? 'pasted image' : 'document'), path: null, preview }]);
        const path = mediaPath(file.name, kind, file.type);
        void readBase64(file)
          .then((content) => api.put(`/projects/${projectId}/file`, { path, content, encoding: 'base64' }))
          .then(() => setItems((list) => list.map((item) => (item.id === id ? { ...item, path } : item))))
          .catch((cause: unknown) => {
            setItems((list) => list.filter((item) => item.id !== id));
            setError(`Could not attach ${file.name || 'that file'}: ${cause instanceof Error ? cause.message : String(cause)}`);
          });
      }
    },
    [projectId],
  );

  const remove = useCallback((id: string) => {
    setItems((list) => {
      const gone = list.find((item) => item.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return list.filter((item) => item.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setItems((list) => {
      for (const item of list) if (item.preview) URL.revokeObjectURL(item.preview);
      return [];
    });
  }, []);

  const uploading = items.some((item) => item.path === null);
  /** The tags for the message, one per line. */
  const tags = items
    .filter((item) => item.path)
    .map((item) => `[${item.kind}: ${item.path}]`)
    .join('\n');
  return { items, add, remove, clear, uploading, tags, error, setError };
}

/** The pictures / documents waiting in the composer, each removable. */
export function AttachmentTray({ items, onRemove }: { items: readonly Attachment[]; onRemove: (id: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="chat-attachments">
      {items.map((item) => (
        <span key={item.id} className={`chat-attachment${item.path ? '' : ' is-uploading'}`} title={item.path ?? 'Uploading…'}>
          {item.preview ? <img src={item.preview} alt="" /> : <Icon name="file" size={12} />}
          <span className="chat-attachment-name">{item.name}</span>
          <button type="button" aria-label={`Remove ${item.name}`} onClick={() => onRemove(item.id)}>
            <Icon name="x" size={10} />
          </button>
        </span>
      ))}
    </div>
  );
}
